import * as THREE from 'three';
import { hash2 } from '../../shared/rng.js';
import { paintBase, whenPhotos } from './photos.js';

// A wall cell stands for a bay about this wide, so a photo tile is scaled to it in metres.
const BAY_M = 3.2;

function canvas(w, h) {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return c;
}

// Canvases made here are read back to add grain, so ask for a software-friendly context.
function context(c) {
  return c.getContext('2d', { willReadFrequently: true });
}

function grain(g, x, y, w, h, amount, seed) {
  const img = g.getImageData(x, y, w, h);
  const d = img.data;
  for (let j = 0; j < h; j++) {
    for (let i = 0; i < w; i++) {
      const k = (j * w + i) * 4;
      const n = (hash2(i, j, seed) - 0.5) * amount;
      d[k] = Math.max(0, Math.min(255, d[k] + n));
      d[k + 1] = Math.max(0, Math.min(255, d[k + 1] + n));
      d[k + 2] = Math.max(0, Math.min(255, d[k + 2] + n));
    }
  }
  g.putImageData(img, x, y);
}

function finish(c, repeat) {
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 8;
  if (repeat) t.wrapS = t.wrapT = THREE.RepeatWrapping;
  return t;
}

const FLAT_NORMAL = '#8080ff'; // a normal map's "straight out", until the photo's relief arrives

// A normal map laid out like the texture it goes with: the photo's relief where there is one, flat
// elsewhere. `cells`: for an atlas, which photo each cell uses; `tile`: one repeated photo.
function normalCanvas(w, h, paint, names) {
  const c = canvas(w, h);
  const g = context(c);
  const draw = () => {
    g.fillStyle = FLAT_NORMAL;
    g.fillRect(0, 0, w, h);
    paint(g);
  };
  draw();
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.NoColorSpace;
  t.anisotropy = 8;
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  whenPhotos(names, () => {
    draw();
    t.needsUpdate = true;
  });
  return t;
}

// The wall atlas's relief: plaster under the plastered cells, block under the block cells.
export function wallNormalAtlas() {
  const S = 256;
  return normalCanvas(S * WALL_COLS, S * WALL_ROWS, (g) => {
    for (let index = 0; index < WALL_COLS * WALL_ROWS; index++) {
      const x = (index % WALL_COLS) * S;
      const y = Math.floor(index / WALL_COLS) * S;
      const block = index === WALL_CELL.block || index === WALL_CELL.blockWindow;
      paintBase(g, block ? 'block-normal' : 'plaster-normal', x, y, S, S, 2.0, S / BAY_M, null, FLAT_NORMAL);
    }
  }, ['plaster-normal', 'block-normal']);
}

export function zincNormal() {
  const S = 256;
  return normalCanvas(S, S, (g) => paintBase(g, 'zinc-normal', 0, 0, S, S, 2.7, S / 2.86, null, FLAT_NORMAL), ['zinc-normal']);
}

export function tarNormal() {
  const W = 256;
  const H = 512;
  return normalCanvas(W, H, (g) => paintBase(g, 'tar-normal', 0, 0, W, H, 3.0, W / 7, null, FLAT_NORMAL), ['tar-normal']);
}

export function earthNormal() {
  const S = 256;
  return normalCanvas(S, S, (g) => paintBase(g, 'earth-normal', 0, 0, S, S, 1.3, S / 6, null, FLAT_NORMAL), ['earth-normal']);
}

// Wall atlas: 4 columns by 2 rows of 128 px cells. The wall's own colour is
// multiplied in from vertex colours, so the cells are near-white.
// Row 0 (plastered): plain, window, door, shop front.
// Row 1: bare block, bare block with window, tall church window, school louvres.
export const WALL_CELL = { plain: 0, window: 1, door: 2, shop: 3, block: 4, blockWindow: 5, church: 6, school: 7 };
export const WALL_COLS = 4;
export const WALL_ROWS = 2;

export function wallAtlas() {
  const S = 256;
  const c = canvas(S * WALL_COLS, S * WALL_ROWS);
  const g = context(c);
  const paint = () => paintWallAtlas(g, S);
  paint();
  const t = finish(c, false);
  t.generateMipmaps = true;
  whenPhotos(['plaster', 'block'], () => {
    paint();
    t.needsUpdate = true;
  });
  return t;
}

function paintWallAtlas(g, S) {
  const k = S / 128; // the joinery below was drawn for 128 px cells
  const cell = (index, draw, base = 'plaster') => {
    const x = (index % WALL_COLS) * S;
    const y = Math.floor(index / WALL_COLS) * S;
    g.save();
    g.translate(x, y);
    // the photo greyed and lifted so the wall's own colour comes from its vertices
    const usedPhoto = base === 'block' ? paintBase(g, 'block', 0, 0, S, S, 2.0, S / BAY_M, 'grayscale(1) brightness(3.8) contrast(0.65)', '#d8d6d2') : paintBase(g, 'plaster', 0, 0, S, S, 2.0, S / BAY_M, 'grayscale(1) brightness(2.15) contrast(0.8)', '#f2efe9');
    g.scale(k, k);
    draw(usedPhoto);
    g.setTransform(1, 0, 0, 1, x, y);
    // damp stain along the foot of the wall
    const fade = g.createLinearGradient(0, S * 0.72, 0, S);
    fade.addColorStop(0, 'rgba(120,80,60,0)');
    fade.addColorStop(1, 'rgba(120,80,60,0.28)');
    g.fillStyle = fade;
    g.fillRect(0, S * 0.72, S, S * 0.28);
    g.restore();
    grain(g, x, y, S, S, usedPhoto ? 6 : 14, index + 3);
  };
  const windowAt = (x, y, w, h) => {
    g.fillStyle = '#d9d4ca';
    g.fillRect(x - 3, y - 3, w + 6, h + 6);
    g.fillStyle = '#39475a';
    g.fillRect(x, y, w, h);
    g.fillStyle = 'rgba(190,215,235,0.35)';
    g.fillRect(x, y, w, h * 0.4);
    g.fillStyle = '#cfc9bd';
    g.fillRect(x + w / 2 - 1, y, 2, h);
    g.fillRect(x, y + h / 2 - 1, w, 2);
  };
  const blocks = (usedPhoto) => {
    if (usedPhoto) return; // the photo has its own mortar lines
    g.strokeStyle = 'rgba(90,90,90,0.35)';
    g.lineWidth = 1;
    for (let row = 0; row < 8; row++) {
      const y = row * 16;
      g.beginPath();
      g.moveTo(0, y + 0.5);
      g.lineTo(128, y + 0.5);
      g.stroke();
      for (let x = row % 2 ? 16 : 0; x < 128; x += 32) {
        g.beginPath();
        g.moveTo(x + 0.5, y);
        g.lineTo(x + 0.5, y + 16);
        g.stroke();
      }
    }
  };
  cell(WALL_CELL.plain, () => {});
  cell(WALL_CELL.window, () => windowAt(40, 34, 48, 50));
  cell(WALL_CELL.door, () => {
    g.fillStyle = '#d9d4ca';
    g.fillRect(44, 36, 40, 92);
    g.fillStyle = '#6b4a34';
    g.fillRect(48, 40, 32, 88);
    g.fillStyle = '#58392a';
    g.fillRect(52, 46, 24, 30);
    g.fillRect(52, 84, 24, 36);
    g.fillStyle = '#d8c070';
    g.fillRect(74, 84, 3, 5);
  });
  cell(WALL_CELL.shop, () => {
    g.fillStyle = '#c9c4ba';
    g.fillRect(0, 12, 128, 22);
    g.fillStyle = '#8b97a3';
    g.fillRect(10, 40, 108, 88);
    g.strokeStyle = 'rgba(40,50,60,0.45)';
    for (let y = 44; y < 128; y += 6) {
      g.beginPath();
      g.moveTo(10, y + 0.5);
      g.lineTo(118, y + 0.5);
      g.stroke();
    }
    g.fillStyle = '#5d6872';
    g.fillRect(10, 40, 108, 4);
  });
  cell(WALL_CELL.block, blocks, 'block');
  cell(
    WALL_CELL.blockWindow,
    (usedPhoto) => {
      blocks(usedPhoto);
      windowAt(42, 36, 44, 46);
    },
    'block'
  );
  cell(WALL_CELL.church, () => {
    g.fillStyle = '#d9d4ca';
    g.beginPath();
    g.moveTo(46, 122);
    g.lineTo(46, 44);
    g.quadraticCurveTo(64, 10, 82, 44);
    g.lineTo(82, 122);
    g.fill();
    g.fillStyle = '#3c5576';
    g.beginPath();
    g.moveTo(51, 118);
    g.lineTo(51, 46);
    g.quadraticCurveTo(64, 20, 77, 46);
    g.lineTo(77, 118);
    g.fill();
    g.fillStyle = 'rgba(230,190,90,0.5)';
    g.fillRect(51, 70, 26, 4);
    g.fillRect(62, 30, 4, 88);
  });
  cell(WALL_CELL.school, () => {
    g.fillStyle = '#d9d4ca';
    g.fillRect(10, 30, 108, 50);
    g.fillStyle = '#44505c';
    g.fillRect(13, 33, 102, 44);
    g.strokeStyle = 'rgba(210,220,225,0.5)';
    g.lineWidth = 2;
    for (let y = 38; y < 76; y += 7) {
      g.beginPath();
      g.moveTo(13, y);
      g.lineTo(115, y);
      g.stroke();
    }
    g.fillStyle = '#d9d4ca';
    g.fillRect(46, 33, 3, 44);
    g.fillRect(80, 33, 3, 44);
  });
}

// The same cells as the wall atlas, black except for the glass: the part of a
// wall that glows when the lights are on inside.
export function windowGlowAtlas() {
  const S = 128;
  const c = canvas(S * WALL_COLS, S * WALL_ROWS);
  const g = context(c);
  g.fillStyle = '#000';
  g.fillRect(0, 0, c.width, c.height);
  const origin = (index) => [(index % WALL_COLS) * S, Math.floor(index / WALL_COLS) * S];
  const pane = (index, x, y, w, h) => {
    const [ox, oy] = origin(index);
    const glow = g.createLinearGradient(0, oy + y, 0, oy + y + h);
    glow.addColorStop(0, '#ffe9b8');
    glow.addColorStop(1, '#e9a850');
    g.fillStyle = glow;
    g.fillRect(ox + x, oy + y, w, h);
    g.fillStyle = '#000';
    g.fillRect(ox + x + w / 2 - 1, oy + y, 2, h);
    g.fillRect(ox + x, oy + y + h / 2 - 1, w, 2);
  };
  pane(WALL_CELL.window, 40, 34, 48, 50);
  pane(WALL_CELL.blockWindow, 42, 36, 44, 46);
  const [cx, cy] = origin(WALL_CELL.church);
  g.fillStyle = '#f3c070';
  g.beginPath();
  g.moveTo(cx + 51, cy + 118);
  g.lineTo(cx + 51, cy + 46);
  g.quadraticCurveTo(cx + 64, cy + 20, cx + 77, cy + 46);
  g.lineTo(cx + 77, cy + 118);
  g.fill();
  return finish(c, false);
}

// Corrugated zinc: light and dark ridges across u. Tinted by vertex colour.
export function zincTexture() {
  const S = 256; // one repeat is 2.86 m of roof (0.35 repeats a metre in the roof's uvs)
  const c = canvas(S, S);
  const g = context(c);
  const paint = () => {
    const used = paintBase(g, 'zinc', 0, 0, S, S, 2.7, S / 2.86, 'grayscale(1) brightness(1.5) contrast(0.9)', '#cdcdcd');
    if (!used) {
      for (let x = 0; x < S; x++) {
        const v = 205 + Math.sin((x / S) * Math.PI * 16) * 34;
        g.fillStyle = `rgb(${v},${v},${v})`;
        g.fillRect(x, 0, 1, S);
      }
    }
    g.fillStyle = 'rgba(60,40,30,0.12)';
    for (let k = 0; k < 14; k++) g.fillRect(hash2(k, 1, 5) * S, hash2(k, 2, 5) * S, (2 + hash2(k, 3, 5) * 5) * 4, (10 + hash2(k, 4, 5) * 30) * 4);
    grain(g, 0, 0, S, S, used ? 6 : 16, 21);
  };
  paint();
  const t = finish(c, true);
  whenPhotos(['zinc'], () => {
    paint();
    t.needsUpdate = true;
  });
  return t;
}

// Tarred road: v runs along the road, u across. Faded centre dashes and worn edges.
export function tarTexture() {
  const W = 256; // across the road (about 7 m), along it 14 m per repeat
  const H = 512;
  const c = canvas(W, H);
  const g = context(c);
  const paint = () => {
    const used = paintBase(g, 'tar', 0, 0, W, H, 3.0, W / 7, 'brightness(0.95) saturate(0.6)', '#4a4b4f');
    if (!used) grain(g, 0, 0, W, H, 26, 31);
    // the left half carries the markings of a main road, the right half is plain tar
    g.save();
    g.scale(W / 256, H / 256);
    paintTarMarks(g, true);
    g.translate(128, 0);
    paintTarMarks(g, false);
    g.restore();
  };
  paint();
  const t = finish(c, true);
  whenPhotos(['tar'], () => {
    paint();
    t.needsUpdate = true;
  });
  return t;
}

function paintTarMarks(g, marked) {
  g.fillStyle = 'rgba(150,90,60,0.35)';
  g.fillRect(0, 0, 9, 256);
  g.fillRect(119, 0, 9, 256);
  if (marked) {
    g.fillStyle = 'rgba(236,232,214,0.8)';
    g.fillRect(62, 20, 4, 90);
    g.fillStyle = 'rgba(236,232,214,0.45)';
    g.fillRect(11, 0, 2, 256);
    g.fillRect(115, 0, 2, 256);
  }
  g.fillStyle = 'rgba(30,30,32,0.5)';
  for (let k = 0; k < 9; k++) {
    g.beginPath();
    g.ellipse(14 + hash2(k, 7, 2) * 100, hash2(k, 8, 2) * 256, 3 + hash2(k, 9, 2) * 7, 2 + hash2(k, 10, 2) * 5, 0, 0, Math.PI * 2);
    g.fill();
  }
}

// Laterite road: red earth with two wheel tracks and loose stones.
export function earthTexture() {
  const S = 256; // one repeat is 6 m along the road and its width across
  const c = canvas(S, S);
  const g = context(c);
  const paint = () => {
    const used = paintBase(g, 'earth', 0, 0, S, S, 1.3, S / 6, 'brightness(1.15)', '#9c6a52');
    if (used) {
      // laterite: the photo's brown pushed to red earth
      g.save();
      g.globalCompositeOperation = 'multiply';
      g.fillStyle = '#c98868';
      g.fillRect(0, 0, S, S);
      g.restore();
    } else grain(g, 0, 0, S, S, 34, 41);
    g.save();
    g.scale(S / 128, S / 128);
    paintEarthMarks(g);
    g.restore();
  };
  paint();
  const t = finish(c, true);
  whenPhotos(['earth'], () => {
    paint();
    t.needsUpdate = true;
  });
  return t;
}

function paintEarthMarks(g) {
  g.fillStyle = 'rgba(122,78,60,0.5)';
  g.fillRect(26, 0, 18, 128);
  g.fillRect(84, 0, 18, 128);
  g.fillStyle = 'rgba(196,140,104,0.5)';
  for (let k = 0; k < 40; k++) g.fillRect(hash2(k, 1, 9) * 128, hash2(k, 2, 9) * 128, 2, 2);
  g.fillStyle = 'rgba(96,120,60,0.35)';
  g.fillRect(0, 0, 5, 128);
  g.fillRect(123, 0, 5, 128);
}

// Wax-print cloth: bold repeating motifs in two inks on a light ground, as the wrappers and kabas of
// the market wear. Four patterns; the cloth's own colour multiplies in from the material.
const WAX_INKS = [
  ['#1b1b1b', '#f2b705'],
  ['#2f2a6b', '#f4f1e8'],
  ['#5a2a1a', '#e8902a'],
  ['#143f2a', '#f4f1e8'],
];
const waxCache = [];
export function waxPrintTexture(variant = 0) {
  const k = variant % WAX_INKS.length;
  if (waxCache[k]) return waxCache[k];
  const S = 256;
  const c = canvas(S, S);
  const g = context(c);
  const [ink, light] = WAX_INKS[k];
  g.fillStyle = light;
  g.fillRect(0, 0, S, S);
  g.fillStyle = ink;
  g.strokeStyle = ink;
  const cell = S / 4;
  for (let row = 0; row < 4; row++) {
    for (let col = 0; col < 4; col++) {
      const x = col * cell;
      const y = row * cell;
      g.save();
      g.translate(x + cell / 2, y + cell / 2);
      if (k === 0) {
        // rings and dots
        g.lineWidth = 6;
        g.beginPath();
        g.arc(0, 0, cell * 0.34, 0, Math.PI * 2);
        g.stroke();
        g.beginPath();
        g.arc(0, 0, cell * 0.1, 0, Math.PI * 2);
        g.fill();
      } else if (k === 1) {
        // diamonds in a lattice
        g.beginPath();
        g.moveTo(0, -cell * 0.42);
        g.lineTo(cell * 0.42, 0);
        g.lineTo(0, cell * 0.42);
        g.lineTo(-cell * 0.42, 0);
        g.closePath();
        g.fill();
        g.fillStyle = light;
        g.beginPath();
        g.moveTo(0, -cell * 0.2);
        g.lineTo(cell * 0.2, 0);
        g.lineTo(0, cell * 0.2);
        g.lineTo(-cell * 0.2, 0);
        g.closePath();
        g.fill();
        g.fillStyle = ink;
      } else if (k === 2) {
        // zigzag bands
        g.lineWidth = 7;
        g.beginPath();
        for (let i = -2; i <= 2; i++) g.lineTo(i * cell * 0.25, (i % 2 ? 1 : -1) * cell * 0.18);
        g.stroke();
        g.beginPath();
        g.arc(0, cell * 0.36, 4, 0, Math.PI * 2);
        g.fill();
      } else {
        // fans and leaves
        g.beginPath();
        g.ellipse(0, 0, cell * 0.38, cell * 0.16, (row + col) % 2 ? 0.8 : -0.8, 0, Math.PI * 2);
        g.fill();
        g.fillStyle = light;
        g.beginPath();
        g.ellipse(0, 0, cell * 0.2, cell * 0.06, (row + col) % 2 ? 0.8 : -0.8, 0, Math.PI * 2);
        g.fill();
        g.fillStyle = ink;
      }
      g.restore();
    }
  }
  grain(g, 0, 0, S, S, 10, 61 + k);
  const t = finish(c, true);
  t.repeat.set(3, 3);
  waxCache[k] = t;
  return t;
}

// Greyscale detail multiplied over the ground so it does not look flat up close: a grass photo
// once it arrives (its brightness about the middle, which the ground shader expects), noise before.
export function detailTexture() {
  const S = 512;
  const c = canvas(S, S);
  const g = context(c);
  const paint = () => paintBase(g, 'grass', 0, 0, S, S, 2.0, S / 9, 'grayscale(1) brightness(0.8) contrast(1.8)', '#969696');
  if (!paint()) paintNoise(g, S);
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.anisotropy = 4;
  whenPhotos(['grass'], () => {
    paint();
    t.needsUpdate = true;
  });
  return t;
}

function paintNoise(g, S) {
  const img = g.createImageData(S, S);
  for (let j = 0; j < S; j++) {
    for (let i = 0; i < S; i++) {
      // sums of wrapped sines keep the texture seamless when it repeats
      const a = Math.sin((i / S) * Math.PI * 2 * 5 + Math.sin((j / S) * Math.PI * 2 * 3) * 2);
      const b = Math.sin((j / S) * Math.PI * 2 * 7 + Math.sin((i / S) * Math.PI * 2 * 4) * 2);
      const v = 150 + a * 22 + b * 22 + (hash2(i, j, 77) - 0.5) * 80;
      const k = (j * S + i) * 4;
      img.data[k] = img.data[k + 1] = img.data[k + 2] = Math.max(0, Math.min(255, v));
      img.data[k + 3] = 255;
    }
  }
  g.putImageData(img, 0, 0);
}

// The glow of a streetlight on the ground: white in the middle, nothing at the edge.
let POOL = null;
export function lampPoolTexture() {
  if (POOL) return POOL;
  const size = 128;
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const g = c.getContext('2d');
  const grad = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  grad.addColorStop(0, 'rgba(255,255,255,1)');
  grad.addColorStop(0.35, 'rgba(255,255,255,0.45)');
  grad.addColorStop(0.75, 'rgba(255,255,255,0.08)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, size, size);
  POOL = new THREE.CanvasTexture(c);
  POOL.colorSpace = THREE.SRGBColorSpace;
  return POOL;
}
